/**
 * Automation > Ejar booking (owner, 21 Oct 2026: Ejar's Create Spot Booking is five screens - here it is one form; all
 * branches, Invygo first; no payment: it is taken on Ejar when the contract is opened; daily and monthly are one form,
 * by the dates; an Invygo booking runs 30 days from the pick-up; no price shown - "مش محتاج السعر اصلا"). Every check
 * is Ejar's own, read live by the engine (api/ejar_booking.py): pick the branch and the dates -> the models Ejar can
 * give; find the customer by phone or identity -> Ejar's checks. Save makes the booking on Ejar under the user's own
 * Ejar login - and only once the engine has Save switched on.
 * Two kinds (owner, 22 Oct 2026): a broker booking - Invygo and the booking sites, Ejar's Rental > Brokers > Create
 * Booking: the broker's own booking number, the broker's price list, the return may be at another branch - and a Spot
 * booking for an individual customer. Invygo is the first broker: at its branch, its return 30 days from the pick-up;
 * any other broker starts at Terminal 1 for one day - the staff changes them when needed.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import { Field, Input, Select, FormGrid } from "../../ui/Field/Field";
import { finesService, errorMessage } from "../../services/finesService";
import EjarAccountCard from "../../components/EjarAccountCard";

const INVYGO_BRANCH = 1502;          // "Business Bay - Invygo" on Ejar
const INVYGO_BROKER = "20f7a178-359b-4787-8c6c-f346a5cd93ca";   // "Invygo Tech FZ LLC" among Ejar's brokers
const INVYGO_DAYS = 30;              // an Invygo booking: 30 days from the pick-up (owner, 21 Oct 2026)
const TERMINAL_1 = 36;               // "Dubai Airport Terminal 1": any other broker's first branch (owner, 22 Oct 2026)
const BROKER = "broker";
const SPOT = "spot";

/** Today in Dubai, "yyyy-mm-dd". */
export const dubaiToday = (now = new Date()) => new Date(now.getTime() + 4 * 3600000).toISOString().slice(0, 10);
/** "yyyy-mm-ddTHH:MM" + days -> the same time that many days later ("" when the time is not a full one). */
export const addDaysAt = (at, days) => {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(String(at || ""))) return "";
    const d = new Date(`${at}:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 16);
};
export const isInvygo = (kind, brokerId) => kind === BROKER && String(brokerId || "").toLowerCase() === INVYGO_BROKER;
/** The return a booking gets by itself: an Invygo booking 30 days from the pick-up; null = the staff picks it. */
export const autoReturn = (kind, brokerId, pickup) => (isInvygo(kind, brokerId) ? addDaysAt(pickup, INVYGO_DAYS) : null);
/** The branch a broker booking starts at (owner, 22 Oct 2026): Invygo at its own branch, any other broker at
 *  Terminal 1 - the staff may change it; null = a Spot booking keeps the branch picked. */
export const brokerBranch = (kind, brokerId) => (kind !== BROKER ? null : isInvygo(kind, brokerId) ? INVYGO_BRANCH : TERMINAL_1);
/** The form's first dates: tomorrow 10:00, back a day later - or 30 days later for Invygo (Dubai). */
export const defaultDates = (today, kind, brokerId) => {
    const pickup = `${addDaysAt(`${today}T10:00`, 1)}`;
    return { pickup, dropoff: autoReturn(kind, brokerId, pickup) || addDaysAt(pickup, 1) };
};
/** Whole days between the two (a part day counts as a day). */
export const daysBetween = (pickup, dropoff) => {
    const ok = (t) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(String(t || ""));
    if (!ok(pickup) || !ok(dropoff)) return 0;
    const a = Date.parse(`${pickup}:00Z`), b = Date.parse(`${dropoff}:00Z`);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return 0;
    return Math.ceil((b - a) / 86400000);
};
/** "2026-11-05 10:00:00" -> "05/11/2026 10:00". */
export const shownTime = (text) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(text || ""));
    return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : String(text || "");
};
const modelKey = (m) => `${m.category_id}|${m.model_id}`;
const TITLE = "text-lg font-semibold text-gray-900";

const Checks = ({ checks }) => (checks || []).length ? (
    <div role="alert" className="flex flex-col gap-1">
        {checks.map((c, i) => (
            <div key={i} className={`text-sm text-center rounded-lg px-3 py-2 border ${c.blocking
                ? "text-red-800 bg-red-50 border-red-200" : "text-amber-800 bg-amber-50 border-amber-200"}`}>
                {c.blocking ? "Ejar refuses: " : "Ejar warns: "}{c.message}
            </div>
        ))}
    </div>
) : null;

/** A new form: a broker booking with Ejar's first broker (Invygo) when Ejar's brokers were read, else a Spot one. */
const blankForm = (lists) => {
    const brokerId = (lists && lists.brokers && lists.brokers.default) || "";
    const kind = brokerId ? BROKER : SPOT;
    const branchId = brokerBranch(kind, brokerId) || (lists && lists.default);
    // dropoff_by_hand: the staff typed the return - it no longer follows the pick-up
    return { kind, broker_id: brokerId, reference: "", branch_id: branchId ? String(branchId) : "",
        dropoff_branch_id: branchId ? String(branchId) : "", ...defaultDates(dubaiToday(), kind, brokerId),
        dropoff_by_hand: false, flight: "", comment: "" };
};

/**
 * ``prefill``: {q, alt, at} - Book on Ejar pressed on Customer registration: that customer is looked up at once by his
 * identity ``q`` (then his phone ``alt`` when the identity finds nobody) and picked when Ejar finds exactly one;
 * ``onPrefillUsed`` clears it so it runs once.
 */
export default function EjarBookingTab({ prefill = null, onPrefillUsed = () => {} }) {
    const [branches, setBranches] = useState({ items: [], default: null, brokers: { items: [] }, save_enabled: false });
    const [f, setF] = useState(() => blankForm(null));
    const [opts, setOpts] = useState({ loading: false, checks: [], models: [], error: "" });
    const [model, setModel] = useState("");
    const [year, setYear] = useState("");
    const [q, setQ] = useState("");
    const [people, setPeople] = useState({ loading: false, items: null, error: "" });
    const [customer, setCustomer] = useState(null);
    const [quote, setQuote] = useState({ loading: false, data: null, error: "" });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [saved, setSaved] = useState(null);
    const optsSeq = useRef(0);
    const quoteSeq = useRef(0);

    useEffect(() => {
        finesService.ejarBookingBranches()
            .then((b) => { setBranches({ ...b, brokers: b.brokers || { items: [] } }); setF(blankForm(b)); })
            .catch((e) => setOpts((o) => ({ ...o, error: errorMessage(e) })));
    }, []);

    const broker = f.kind === BROKER;
    const brokers = branches.brokers || { items: [] };
    const branch = branches.items.find((b) => String(b.id) === String(f.branch_id));
    const fixedReturn = autoReturn(f.kind, f.broker_id, f.pickup);
    const days = daysBetween(f.pickup, f.dropoff);
    // what tells Ejar which booking this is: the broker and the return branch, or nothing for a Spot booking
    const kindBody = useMemo(() => (broker ? { kind: BROKER, broker_id: f.broker_id, dropoff_branch_id: Number(f.dropoff_branch_id) } : {}),
        [broker, f.broker_id, f.dropoff_branch_id]);

    // the branch, the broker or the dates changed: Ejar's checks and the models it can give
    useEffect(() => {
        if (!f.branch_id || !f.pickup || !f.dropoff || (broker && !f.broker_id)) return undefined;
        const seq = ++optsSeq.current;
        setOpts((o) => ({ ...o, loading: true, error: "" }));
        const t = setTimeout(() => {
            finesService.ejarBookingOptions({ branch_id: Number(f.branch_id), pickup: f.pickup, dropoff: f.dropoff, ...kindBody })
                .then((r) => { if (seq === optsSeq.current) setOpts({ loading: false, checks: r.checks || [], models: r.models || [], error: "" }); })
                .catch((e) => { if (seq === optsSeq.current) setOpts({ loading: false, checks: [], models: [], error: errorMessage(e) }); });
        }, 400);
        return () => clearTimeout(t);
    }, [f.branch_id, f.pickup, f.dropoff, broker, f.broker_id, kindBody]);

    const picked = opts.models.find((m) => modelKey(m) === model);
    // the chosen model is gone from the new list: choose again; its year: the newest
    useEffect(() => {
        if (model && !picked && !opts.loading) { setModel(""); setYear(""); }
        else if (picked && !picked.years.includes(Number(year))) setYear(picked.years[0] ? String(picked.years[0]) : "");
    }, [model, picked, year, opts.loading]);

    // everything picked (a broker booking: and the broker's booking number): Ejar's checks
    const reference = f.reference.trim();
    // (not while the car list is reloading: the chosen car may not be in the new broker's / branch's list)
    const quoteBody = useMemo(() => (picked && year && customer && !opts.loading && (!broker || reference) ? {
        branch_id: Number(f.branch_id), pickup: f.pickup, dropoff: f.dropoff, category_id: picked.category_id,
        model_id: picked.model_id, year: Number(year), customer_id: customer.id, lite_id: customer.lite_id,
        ...kindBody, ...(broker ? { reference } : {}),
    } : null), [picked, year, customer, opts.loading, broker, reference, kindBody, f.branch_id, f.pickup, f.dropoff]);
    useEffect(() => {
        const seq = ++quoteSeq.current;              // an answer still on its way is for an older form: dropped
        if (!quoteBody) { setQuote({ loading: false, data: null, error: "" }); return undefined; }
        setQuote({ loading: true, data: null, error: "" });
        const t = setTimeout(() => {                 // the booking number is typed: ask once it rests
            finesService.ejarBookingQuote(quoteBody)
                .then((r) => { if (seq === quoteSeq.current) setQuote({ loading: false, data: r, error: "" }); })
                .catch((e) => { if (seq === quoteSeq.current) setQuote({ loading: false, data: null, error: errorMessage(e) }); });
        }, 400);
        return () => clearTimeout(t);
    }, [quoteBody]);

    // the kind or the broker changed: Invygo starts at its branch, 30 days; any other broker at Terminal 1, one day
    // (owner, 22 Oct 2026) - until the staff changes them. The return branch follows the pick-up branch; Invygo's return
    // follows the pick-up, another broker's stays a day after it until the staff types the return
    const set = (k) => (e) => {
        const value = e.target.value;
        setSaveError(""); setSaved(null);
        setF((x) => {
            const next = { ...x, [k]: value };
            if (k === "dropoff") next.dropoff_by_hand = true;
            const startBranch = (k === "kind" || k === "broker_id") && brokerBranch(next.kind, next.broker_id);
            if (startBranch) {
                next.branch_id = String(startBranch);
                next.dropoff_by_hand = false;
            }
            if (k === "branch_id" || next.branch_id !== x.branch_id) next.dropoff_branch_id = next.branch_id;
            if (k === "kind" || k === "broker_id" || k === "pickup") {
                const oneDay = next.kind === BROKER && !next.dropoff_by_hand ? addDaysAt(next.pickup, 1) : "";
                next.dropoff = autoReturn(next.kind, next.broker_id, next.pickup) || oneDay || next.dropoff;
            }
            return next;
        });
    };
    const findCustomer = async (e, typed) => {
        if (e) e.preventDefault();
        const text = String(typed ?? q).trim();
        if (!text) return;
        setCustomer(null); setSaveError(""); setSaved(null);
        setPeople({ loading: true, items: null, error: "" });
        try {
            const r = await finesService.ejarBookingCustomer(text);
            const items = r.items || [];
            setPeople({ loading: false, items, error: "" });
            if (items.length === 1) setCustomer(items[0]);
            return items;
        } catch (x) { setPeople({ loading: false, items: null, error: errorMessage(x) }); return null; }
    };
    // from Customer registration: the customer typed in and looked up by itself
    useEffect(() => {
        if (!prefill || !(prefill.q || prefill.alt)) return;
        const first = prefill.q || prefill.alt;
        const alt = prefill.q && prefill.alt && prefill.alt !== prefill.q ? prefill.alt : "";
        setQ(first);
        findCustomer(null, first).then((items) => {
            if (alt && items && !items.length) { setQ(alt); findCustomer(null, alt); }
        });
        onPrefillUsed();
    }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

    const startOver = () => {
        setF(blankForm(branches)); setModel(""); setYear(""); setQ("");
        setPeople({ loading: false, items: null, error: "" }); setCustomer(null); setSaveError("");
    };
    const save = async () => {
        if (!quoteBody) return;
        setSaving(true); setSaveError("");
        try {
            const r = await finesService.ejarBookingCreate({ ...quoteBody, flight: f.flight, comment: f.comment });
            setSaved(r);
            startOver();
        } catch (x) { setSaveError(errorMessage(x)); }
        setSaving(false);
    };

    const data = quote.data;
    const canSave = branches.save_enabled && data && data.can_save && !saving;
    const blockedDates = opts.checks.some((c) => c.blocking);
    return (
        <div className="space-y-4">
            {/* the booking is saved on Ejar under the employee's OWN Ejar login */}
            <EjarAccountCard />
            <Card>
                <div className="flex flex-col gap-4">
                    <div className="text-center">
                        <h2 className={TITLE}>New booking on Ejar</h2>
                        <p className="text-sm text-gray-700">One form instead of Ejar's five screens. The checks are Ejar's own; no payment now - it is taken on Ejar when the contract is opened.</p>
                        {!branches.save_enabled && <p className="text-sm font-medium text-amber-700 mt-1">Test mode: Save is switched off - nothing is made on Ejar</p>}
                    </div>
                    {saved && (
                        <div role="status" className="text-sm text-center rounded-lg px-3 py-2 border text-green-800 bg-green-50 border-green-200">
                            Booked on Ejar{saved.booking_number ? `: ${saved.booking_number}` : ""}{saved.reference ? ` · broker's number ${saved.reference}` : ""} · {saved.branch} · {shownTime(saved.pickup)} → {shownTime(saved.dropoff)}
                        </div>
                    )}

                    <FormGrid cols={2}>
                        <Field label="Booking type" required>
                            <Select value={f.kind} onChange={set("kind")}>
                                <option value={BROKER} disabled={!brokers.items.length}>Broker booking (Invygo, booking sites)</option>
                                <option value={SPOT}>Spot booking (individual customer)</option>
                            </Select>
                        </Field>
                        {broker
                            ? <Field label="Broker" required>
                                <Select value={f.broker_id} onChange={set("broker_id")}>
                                    {brokers.items.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </Select>
                            </Field>
                            : <div />}
                        {broker && (
                            <Field label="Broker's booking number" required help="Ejar's Broker Booking Reference No - the number the broker gave this booking">
                                <Input value={f.reference} onChange={set("reference")} placeholder="Invygo's booking number" autoComplete="off" />
                            </Field>
                        )}
                        {broker && <div />}
                        <Field label={broker ? "Pick-up branch" : "Branch"} required>
                            <Select value={f.branch_id} onChange={set("branch_id")}>
                                {branches.items.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </Select>
                        </Field>
                        {broker
                            ? <Field label="Return branch" required>
                                <Select value={f.dropoff_branch_id} onChange={set("dropoff_branch_id")}>
                                    {branches.items.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </Select>
                            </Field>
                            : branch && branch.airport
                                ? <Field label="Flight number" optional><Input value={f.flight} onChange={set("flight")} placeholder="EK 202" /></Field>
                                : <div />}
                        {broker && branch && branch.airport && (
                            <Field label="Flight number" optional><Input value={f.flight} onChange={set("flight")} placeholder="EK 202" /></Field>
                        )}
                        {broker && branch && branch.airport && <div />}
                        <Field label="Pick-up" required><Input type="datetime-local" value={f.pickup} onChange={set("pickup")} /></Field>
                        <Field label={broker ? "Return" : "Return (same branch)"} required
                            help={fixedReturn ? `Invygo: ${INVYGO_DAYS} days from the pick-up` : days ? `${days} day${days > 1 ? "s" : ""}${broker ? "" : " - up to 30 on one booking"}` : undefined}>
                            <Input type="datetime-local" value={f.dropoff} min={f.pickup} onChange={set("dropoff")} />
                        </Field>
                    </FormGrid>
                    {brokers.error && <Checks checks={[{ message: `Ejar's brokers could not be read (${brokers.error}) - only a Spot booking can be made now`, blocking: false }]} />}
                    {/* the Invygo bookings made as Spot here on 21-22 Oct had to be cancelled and made again as Broker ones */}
                    {!broker && Number(f.branch_id) === INVYGO_BRANCH && (
                        <div className="text-sm text-center rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
                            An Invygo booking is a Broker booking on Ejar - pick "Broker booking" above
                        </div>
                    )}
                    {opts.error && <Checks checks={[{ message: opts.error, blocking: true }]} />}
                    <Checks checks={opts.checks} />

                    <FormGrid cols={2}>
                        <Field label="Car model" required help={opts.loading ? "Asking Ejar which cars this branch can give…" : undefined}>
                            <Select value={model} onChange={(e) => { setModel(e.target.value); setYear(""); }} disabled={opts.loading || blockedDates || !opts.models.length}>
                                <option value="">{opts.loading ? "Loading…" : blockedDates || opts.error ? "Fix the branch / dates first"
                                    : opts.models.length ? "Pick a model" : "No car free on these dates"}</option>
                                {opts.models.map((m) => <option key={modelKey(m)} value={modelKey(m)}>{m.model} · {m.category}</option>)}
                            </Select>
                        </Field>
                        <Field label="Year" required>
                            <Select value={year} onChange={(e) => setYear(e.target.value)} disabled={!picked}>
                                {(picked ? picked.years : []).map((y) => <option key={y} value={y}>{y}</option>)}
                            </Select>
                        </Field>
                    </FormGrid>

                    <form onSubmit={findCustomer} className="flex flex-col gap-2">
                        <Field label="Customer (mobile or identity number)" required>
                            <div className="flex gap-2">
                                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="050 123 4567 or 784-…" inputMode="text" />
                                <Button type="submit" isLoading={people.loading}>Find</Button>
                            </div>
                        </Field>
                        {people.error && <Checks checks={[{ message: people.error, blocking: true }]} />}
                        {people.items && !people.items.length && (
                            <div className="text-sm text-center rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
                                Not on Ejar. Register the customer first (the Customer registration tab), then find again.
                            </div>
                        )}
                        {people.items && people.items.length > 1 && (
                            <div className="flex flex-col gap-1" role="radiogroup" aria-label="Which customer">
                                {people.items.map((p) => (
                                    <label key={p.lite_id} className={`flex items-center justify-center gap-3 text-sm rounded-lg border px-3 py-2 cursor-pointer ${customer && customer.lite_id === p.lite_id ? "border-blue-500 bg-blue-50" : "border-gray-200"}`}>
                                        <input type="radio" name="ejar-customer" checked={!!customer && customer.lite_id === p.lite_id} onChange={() => setCustomer(p)} />
                                        <strong>{p.name || "—"}</strong><span className="tabular-nums">{p.phone}</span>{p.membership && <span>{p.membership}</span>}
                                    </label>
                                ))}
                            </div>
                        )}
                        {customer && (people.items || []).length === 1 && (
                            <div className="text-sm text-center rounded-lg px-3 py-2 border border-blue-200 bg-blue-50">
                                <strong>{customer.name || "—"}</strong> · <span className="tabular-nums">{customer.phone}</span>{customer.membership ? ` · ${customer.membership}` : ""}
                            </div>
                        )}
                        {customer && customer.blocked && <Checks checks={[{ message: "This customer's membership is blocked on Ejar", blocking: false }]} />}
                    </form>

                    {/* Ejar's checks only - no price (owner, 21 Oct 2026: "مش محتاج السعر اصلا") */}
                    {quote.loading && <div className="text-sm text-center text-gray-700">Asking Ejar…</div>}
                    {quote.error && <Checks checks={[{ message: quote.error, blocking: true }]} />}
                    {data && <Checks checks={data.checks} />}
                    {broker && picked && year && customer && !reference && (
                        <div className="text-sm text-center text-gray-700">Type the broker's booking number for Ejar's checks</div>
                    )}
                    {data && data.can_save && (
                        <div role="status" className="text-sm text-center font-medium rounded-lg px-3 py-2 border text-green-800 bg-green-50 border-green-200">
                            Ejar accepts this booking · {data.branch}{data.return_branch && data.return_branch !== data.branch ? ` → ${data.return_branch}` : ""} · {shownTime(data.pickup)} → {shownTime(data.dropoff)}
                        </div>
                    )}

                    <Field label="Comment" optional><Input value={f.comment} onChange={set("comment")} placeholder="Shown on the booking on Ejar" /></Field>
                    {saveError && <Checks checks={[{ message: saveError, blocking: true }]} />}
                    <div className="flex justify-center gap-2">
                        <Button onClick={startOver}>Start over</Button>
                        <Button variant="primary" onClick={save} isLoading={saving} disabled={!canSave}
                            title={branches.save_enabled ? "Make this booking on Ejar under your own Ejar login" : "Save is switched off (test)"}>
                            Create booking on Ejar
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}
